import ChainLogo from '@components/ChainLogo'
import useToast from '@hooks/useToast'
import useEvmChain from '@plug-core/hooks/useEVMChain'
import useWallet from '@plug-core/hooks/useWallet'
import { Chain } from '@plug-core/interfaces/common'
import { useUserEVMChains } from '@plug-core/state'
import { shortAddress } from '@plug-core/utils/shortAddress'
import { IconButton, Text } from '@ui'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { IconType } from 'react-icons'
import { TbCopy } from 'react-icons/tb'
import { useNavigate } from 'react-router-dom'

export const Address = ({
  chain,
  title,
  address,
  navigateOnCopy,
  actions,
}: {
  chain: string
  title?: string
  address?: string
  navigateOnCopy?: boolean
  actions?: Array<{
    icon: IconType
    onClick: () => void
    /** Accessible name for the icon button. */
    label?: string
  }>
}) => {
  const navigate = useNavigate()
  const { setToastMessage } = useToast()
  const { t } = useTranslation()
  const evmChain = useEvmChain(chain)

  const onCopy = () => {
    navigator.clipboard.writeText(address || '')
    setToastMessage(t('deposit.addressCopied'))

    if (navigateOnCopy !== false) {
      navigate(-1)
    }
  }

  const name = evmChain?.name || title

  return (
    <div className="mb-2 flex items-center gap-2 rounded-lg border border-solid border-hairline bg-surface pr-3 transition-colors duration-150 hover:bg-surface-2">
      <button
        type="button"
        onClick={onCopy}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-4 rounded-lg border-0 bg-transparent py-3 pl-3 text-left font-text text-fg focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus"
      >
        <ChainLogo chain={chain} size={28} />
        <span className="flex min-w-0 flex-col">
          <Text as="span" variant="body-s" weight="semibold" truncate>
            {name}
          </Text>
          <Text as="span" variant="mono" tone="fg-2" className="text-[12px]" truncate>
            {shortAddress(address || '', { rightSize: 8, leftSize: 8 })}
          </Text>
        </span>
      </button>

      <div className="ml-auto flex shrink-0 items-center gap-1">
        <IconButton
          aria-label={`${t('common.copy')} ${name || ''}`.trim()}
          variant="secondary"
          size="sm"
          onClick={onCopy}
        >
          <TbCopy />
        </IconButton>

        {actions?.map((action, i) => (
          <IconButton
            key={i}
            aria-label={action.label || t('common.manage')}
            variant="secondary"
            size="sm"
            onClick={action.onClick}
          >
            <action.icon />
          </IconButton>
        ))}
      </div>
    </div>
  )
}

export default () => {
  const { wallet } = useWallet()
  const { userEVMChains } = useUserEVMChains()
  const { t } = useTranslation()

  return (
    <div className="mb-3 flex flex-col">
      <Address chain={Chain.BTC} title={t('addresses.btcAddress')} address={wallet.btcAddress} />
      <Address chain={Chain.SOL} title={t('addresses.solAddress')} address={wallet.solAddress} />
      <Address chain={Chain.DOGE} title={t('addresses.dogeAddress')} address={wallet.dogeAddress} />
      <Address chain={Chain.ICP} title={t('addresses.icpAccount')} address={wallet.accountId} />
      <Address chain={Chain.ICP} title={t('addresses.icpPrincipal')} address={wallet.principalId} />
      <Address chain={Chain.ODIN} title={t('addresses.odinAddress')} address={wallet.principalId} />
      {userEVMChains.map((chain) => (
        <Address key={chain} chain={chain.toString()} address={wallet.ethAddress} />
      ))}
    </div>
  )
}
